'use client';
import { useEffect, useState } from 'react';
import { freePlayerHref, openPlayer } from '../../player/player-shared';
import { fmtDate } from '../../../lib/datetime';
import { useT } from '../../../lib/i18n';
import type { ListStyleBlock, ProductDetail, RelatedProducts, ReviewStats } from '../../../lib/site';
import { Icon } from '../../Icon';
import { CourseGrid } from '../CourseGrid';
import { ProductReviews } from '../../reviews/ProductReviews';
import { ClassroomDetail } from '../../mypage/ClassroomDetail';

/** 초 → `h:mm:ss`(가이드 `0:10:34`) */
function runtime(sec: number | null): string {
  if (!sec) return '-';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

type TabKey = 'study' | 'intro' | 'lec' | 'related' | 'review';

/**
 * 상세 탭 — 가이드 `lecture-tab-wrap` + `bd-tab-panel` 넷.
 *
 * **순서는 상품소개 · 강의 · 연관상품 · 수강후기**(2026-08-22 사용자 확정).
 * 가이드 견본은 강의를 먼저 열어 뒀지만 순서를 이렇게 정했으므로 **첫 탭(상품소개)이
 * 기본으로 열린다** — 순서를 정해 놓고 다른 탭이 열려 있으면 순서가 뜻을 잃는다.
 *
 * 후기 탭은 **상품 설정으로 끈다**(`reviewEnabled`) — 끈 상품은 탭 자체를 그리지 않는다.
 *
 * **구독으로 열리는 강좌는 [수강하기] 가 맨 앞이고 [강의] 가 빠진다**(2026-09-09 사용자 확정).
 * 들을 수 있는 사람에게 목차를 다시 보여 줄 이유가 없다 — [수강하기] 탭이 차시 표를 이미
 * 그리고, 그쪽에는 진도·[스트리밍] 까지 있다. 나머지 탭은 뒤로 밀린다.
 */
export function PdTabs({ p, style, bookStyle, priceDetail, stats, related, reviewEnabled, reviewView, canWriteReview, initialTab, covered }: {
  p: ProductDetail;
  /** 이 상세가 쓰는 목록 설정 — 강좌 상세면 강좌 것, 교재 상세면 교재 것 */
  style: ListStyleBlock;
  /**
   * 연관 **교재** 묶음이 쓸 설정(2026-08-27 사용자 지적 "교재리스트의 디자인이여야 하는데").
   * 강좌 상세에서도 교재는 교재 목록의 모양으로 그려야 한다 — 카드 비율·한 줄 개수가 다르다.
   */
  bookStyle: ListStyleBlock;
  priceDetail: boolean;
  stats: ReviewStats;
  related: RelatedProducts;
  reviewEnabled: boolean;
  /** 후기 기본 뷰(마켓 설정) — 목록 화면과 같은 값을 쓴다 */
  reviewView: 'card' | 'list';
  /** 후기를 쓸 수 있나 — 서버가 게시판 권한으로 판정해 준다 */
  canWriteReview: boolean;
  /** 처음 열 탭 — 주소(`?tab=`)가 정한다. 없으면 상품소개(구독으로 열리면 수강하기) */
  initialTab?: TabKey;
  /** 이 강좌가 내 구독으로 열리는가 — [수강하기] 탭이 생기고 [강의] 탭이 빠진다 */
  covered?: boolean;
}) {
  const t = useT();
  /*
   * 첫 탭 — 주소에 `?tab=review` 가 있으면 후기부터 연다(2026-09-07). 후기를 보다 [목록으로]
   * 돌아왔을 때 상품소개로 튕기면 보던 자리를 잃는다.
   */
  const [tab, setTab] = useState<TabKey>(initialTab ?? (covered ? 'study' : 'intro'));

  const relatedCount = related.lecture.length + related.book.length;
  /*
   * **교재는 탭이 둘뿐이다**(2026-08-23 사용자 확정) — 교재소개·연관상품.
   * 강의 탭은 교재에 뜻이 없고, 목차·구매후기는 영구 제거다(가이드 `book-detail.html` 에
   * 견본이 있으나 쓰지 않기로 정했다).
   */
  const isBook = p.type === 'BOOK';
  /** 연관상품이 강좌·교재 두 갈래로 나뉘는가 — 나뉠 때만 묶음 제목을 단다 */
  const split = related.lecture.length > 0 && related.book.length > 0;

  /** 구독으로 열리는 강좌 — 맨 앞에 [수강하기], [강의] 는 뺀다(교재는 애초에 강의 탭이 없다) */
  const study = !!covered && !isBook;
  const TABS: { key: TabKey; label: string; cnt?: number }[] = [
    ...(study ? [{ key: 'study' as TabKey, label: t('prd.tab.study') }] : []),
    { key: 'intro', label: t(isBook ? 'prd.tab.introBook' : 'prd.tab.intro') },
    ...(isBook || study ? [] : [{ key: 'lec' as TabKey, label: t('prd.tab.lesson') }]),
    { key: 'related', label: t('prd.tab.related'), cnt: relatedCount },
    // 후기를 끈 상품은 **탭 자체가 없다** — 눌러서 빈 화면을 보게 두지 않는다
    ...(reviewEnabled ? [{ key: 'review' as TabKey, label: t('prd.tab.review'), cnt: stats.count }] : []),
  ];

  return (
    <>
      <div className="lecture-tab-wrap">
        {TABS.map((x) => (
          <button key={x.key} className={`lecture-tab${tab === x.key ? ' active' : ''}`} onClick={() => setTab(x.key)}>
            {x.label}{x.cnt != null && <span className="tab-cnt">{x.cnt}</span>}
          </button>
        ))}
      </div>

      {/*
        수강하기 — **나의 강의실 수강 상세와 같은 부품**(2026-09-09 사용자 확정 "모듈화").
        두 벌로 두면 패스·패키지·단과 UI 가 한쪽만 고쳐진다. 즐겨찾기·수강확인서 같은
        수강권이 근거인 기능은 그 부품이 스스로 감춘다.
      */}
      {tab === 'study' && (
        <div className="bd-tab-panel">
          <ClassroomDetail source={{ kind: 'billing', productId: p.id }} />
        </div>
      )}

      {tab === 'lec' && <LectureTab p={p} />}

      {tab === 'intro' && (
        <div className="bd-tab-panel">
          {p.contents?.trim()
            // 서버가 저장할 때 sanitize 한 HTML(§16-8) — 그대로 그린다
            ? <div className="bd-desc-box" dangerouslySetInnerHTML={{ __html: p.contents }} />
            : <div className="cp-empty"><Icon name="file-text" /><p>{t('prd.noIntro')}</p></div>}
        </div>
      )}

      {/*
        후기 — **후기 목록 화면과 같은 부품**을 쓴다(2026-09-07 사용자 지시 "공통 모듈로 한 군데서").
        전에는 이 자리에 자기 마크업이 있어 한쪽만 고치면 둘이 갈렸다(베스트가 빠지고
        작성자가 비어 있던 것이 그래서다). 강좌 코드만 넘긴다.
      */}
      {tab === 'review' && (
        <div className="bd-tab-panel">
          <ProductReviews productId={p.id} stats={stats} base="/boards/review"
            defaultView={reviewView} canWrite={canWriteReview}
            backTo={`/lectures/${p.id}?tab=review`} />
        </div>
      )}

      {tab === 'related' && (
        <div className="bd-tab-panel">
          {relatedCount === 0 ? (
            <div className="cp-empty"><Icon name="search-x" /><p>{t('prd.noRelated')}</p></div>
          ) : (
            <>
              {/*
                강좌·교재 두 섹션 — 서버가 갈라서 준다(2026-08-22 확정 규칙).

                **제목은 둘 다 있을 때만 붙인다**(2026-08-27 사용자 지시). 한 갈래뿐이면
                탭 이름이 곧 제목이라 같은 말을 또 적는 셈이고, 둘이 이어 붙으면 어디서부터
                교재인지 알려 줘야 한다.
              */}
              {related.lecture.length > 0 && (
                <>
                  {split && (
                    <div className="pd-sec-title"><span className="lec-sec-bar"></span>{t('prd.group.lecture')}</div>
                  )}
                  {/* 연관 탭에는 조작 UI 가 없다 — 기억할 취향도 없지만 자리 이름은 갈라 둔다 */}
                  <CourseGrid items={related.lecture} style={style} priceDetail={priceDetail}
                    storageKey={`related:lecture:${p.id}`} />
                </>
              )}
              {related.book.length > 0 && (
                <>
                  {split && (
                    <div className="pd-sec-title mt25"><span className="lec-sec-bar"></span>{t('prd.group.book')}</div>
                  )}
                  <CourseGrid items={related.book} style={bookStyle} priceDetail={priceDetail}
                    storageKey={`related:book:${p.id}`} />
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

/**
 * 강의 탭 — 가이드 `pd-course-select-row` + `pd-lec-box`.
 *
 * **묶음은 구성 단과를 골라** 그 차시를 본다(차시가 단과 소속이라서다). 단과는 고를 것이
 * 없으므로 셀렉트를 그리지 않고 자기 차시를 바로 펼친다.
 */
function LectureTab({ p }: { p: ProductDetail }) {
  const t = useT();
  const lectures = p.childMappings.filter((m) => m.child.type !== 'BOOK');
  const [at, setAt] = useState(0);
  const rows = p.curriculumMaps.length
    ? p.curriculumMaps
    : (lectures[at]?.child.curriculumMaps ?? []);

  return (
    <div className="bd-tab-panel">

      {p.curriculumMaps.length === 0 && lectures.length > 0 && (
        <div className="pd-course-select-row">
          <select className="guide-select w100" value={at} onChange={(e) => setAt(Number(e.target.value))}>
            {lectures.map((m, i) => (
              <option key={m.child.id} value={i}>{m.child.name}</option>
            ))}
          </select>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="cp-empty"><Icon name="search-x" /><p>{t('prd.noLesson')}</p></div>
      ) : (
        <div className="pd-lec-box">
          <div className="pd-lec-head">
            <div>{t('prd.lec.no')}</div>
            <div>{t('prd.lec.name')}</div>
            <div>{t('prd.lec.time')}</div>
            <div>{t('prd.lec.preview')}</div>
          </div>
          {rows.map((m, i) => (
            <div className="pd-lec-row" key={m.curriculumId}>
              <div className="pd-lec-no">{m.sort ?? i + 1}</div>
              <div className="pd-lec-name">{m.curriculum.name}</div>
              <div className="pd-lec-time">{runtime(m.curriculum.runtime)}</div>
              <div className="pd-lec-preview">
                {/*
                  맛보기 차시만 ▶ — 플레이어를 제 창에 띄운다(로그인 불필요). 아닌 차시에는 버튼 자체를 두지 않는다.
                  주소의 `pid` 는 **서버가 준 그 차시의 주인 상품**이다(2026-09-17) — 패키지면 구성 단과 번호다.
                  전에는 지금 고른 탭으로 짐작했는데, 서버가 자격을 `(pid, ccid)` 짝으로 다시 보므로 짐작이 틀리면 막힌다.
                */}
                {m.free && (
                  <button className="pd-play-btn" type="button" aria-label={t('prd.play')}
                    onClick={() => openPlayer(freePlayerHref(m.productId, m.curriculumId), { panel: false })}>
                    <Icon name="play" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

